/// <reference types="vite/client" />
/**
 * RecentFailuresPanel — polls /api/swarm-actions/recent-failures every
 * 10s and shows the 10 most recent rows. Empty state when 0 failures.
 */

import { useCallback, useEffect, useState } from "react";
import { useAgentSocketContext } from "../hooks/useAgentSocketContext";

interface FailureRow {
  id: string;
  action: string;
  agent: string;
  engagement: string;
  created_at: string;
  error_message: string;
  details: Record<string, unknown>;
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return `${Math.floor(ms / 1000)}s ago`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
}

const AGENT_COLOR: Record<string, string> = {
  marketing: "var(--color-accent)",
  support: "rgb(56, 189, 248)",
  integrator: "var(--color-accent-bright)",
  growth: "rgb(168, 85, 247)",
  ceo: "rgb(132, 204, 22)",
};

export function RecentFailuresPanel() {
  const { subscribe } = useAgentSocketContext();
  const [rows, setRows] = useState<FailureRow[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch("/api/swarm-actions/recent-failures?limit=10");
      if (!res.ok) return;
      const { failures } = (await res.json()) as { failures: FailureRow[] };
      setRows(failures);
      setLoaded(true);
    } catch {
      // swallow — panel stays in last good state
    }
  }, []);

  // Phase 13 U2 #294 — bootstrap once, then refresh on system.action_failed
  // realtime events (emitted by logFailure).
  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const unsub = subscribe("system.action_failed", () => { void load(); });
    return () => { unsub(); };
  }, [subscribe, load]);

  return (
    <section className="space-y-3">
      <div className="section-rule flex items-center justify-between">
        <p className="eyebrow">// Recent failures</p>
        {loaded && rows.length === 0 && (
          <span className="font-mono text-[10px] uppercase tracking-wider" style={{ color: "var(--color-status-active)" }}>
            ● All systems nominal
          </span>
        )}
        {rows.length > 0 && (
          <span className="font-mono text-[10px] uppercase tracking-wider" style={{ color: "var(--color-status-error)" }}>
            ● {rows.length} failure{rows.length > 1 ? "s" : ""}
          </span>
        )}
      </div>

      {loaded && rows.length === 0 && (
        <div className="panel p-6 text-center">
          <p className="font-mono text-[11px] text-[var(--color-text-dim)] tracking-wider uppercase">
            Aucune action en échec
          </p>
        </div>
      )}

      {rows.length > 0 && (
        <div className="space-y-2">
          {rows.map((r) => (
            <div
              key={r.id}
              className="panel p-3 cursor-pointer"
              onClick={() => setExpanded(expanded === r.id ? null : r.id)}
              style={{ background: "var(--color-bg-card)" }}
            >
              <div className="flex items-center gap-2 sm:gap-3 text-sm min-w-0">
                <span
                  className="pill shrink-0"
                  style={{ color: AGENT_COLOR[r.agent] ?? "var(--color-text-muted)", borderColor: "var(--color-border)", background: "var(--color-bg)" }}
                >
                  {r.agent}
                </span>
                <span className="font-mono text-xs text-[var(--color-text)] truncate min-w-0">{r.action}</span>
                <span className="ml-auto font-mono text-[10px] text-[var(--color-text-dim)] tabular-nums shrink-0">{timeAgo(r.created_at)}</span>
              </div>
              <p className="mt-1 font-mono text-[11px] text-[var(--color-text-muted)] truncate">
                {r.error_message || "(no message)"}
              </p>
              {expanded === r.id && (
                <pre className="mt-2 p-2 rounded font-mono text-[10px] overflow-x-auto" style={{ background: "var(--color-bg)", color: "var(--color-text-muted)", border: "1px solid var(--color-border)" }}>
                  {JSON.stringify(r.details, null, 2)}
                </pre>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
